import { fetchData, fetchWithArgs, postData } from '@/utils/requests';
import { create } from 'zustand';
import Cookies from "universal-cookie";

const queryString = window.location.search;
export const urlParams = new URLSearchParams(queryString);
const cookies = new Cookies();

interface UserLogged {
  id: number | null;
  email: string | null;
  logged: boolean;
  premium: boolean;
  affiliate_id: string | null;
  BLRef: boolean;
  expire_at: number | null;
  refreshed: boolean;
  payment_info: any | null;
  cctk: string | null;
  cctk_expires_in: number;
  subscription_period: string | null;
  payment_date: number | null;

  set: (user: UserLogged | Partial<UserLogged>) => void;
  setBLRef: (BLRef: boolean) => void;
}


export const userDefault = { id: null, email: null, logged: false, premium: false, expire_at: null, BLRef: !!urlParams.get("gclid"), payment_info: null };

export const useUserStore = create<UserLogged>((set) => ({
  id: null,
  email: null,
  logged: false,
  premium: false,
  affiliate_id: null,
  expire_at: null,
  refreshed: false,
  payment_info: null,
  subscription_period: null,
  BLRef: !!urlParams.get("gclid"),
  set: (newUser) => set(newUser),
  setBLRef: (BLRef: boolean) => set({ BLRef: BLRef }),
  cctk: null,
  cctk_expires_in: 0,
  payment_date: null,
}));

export const AffiliateId = create<{
  affiliate_id: string | null;
  setAffiliateId: (id: string) => void;
}>((set) => ({
  affiliate_id: urlParams.get('a') || cookies.get('a') || null,
  setAffiliateId: (id: string) => set({ affiliate_id: id })
}));


interface AfterRefreshCallback {
  (): void;
}

var afterRefresh: AfterRefreshCallback[] = [];

interface RefreshListener {
  (fn: () => void): void;
}

export const addRefreshListener: RefreshListener = (fn) => {
  const userLogged = useUserStore.getState();
  if (userLogged.refreshed) fn();
  else afterRefresh.push(fn);
}


const setRefreshed = (_data: any = null) => {
  const userLogged = useUserStore.getState();
  userLogged.set({ ...userLogged, refreshed: true });
  afterRefresh.forEach(fn => fn());
  afterRefresh = [];
};

var cctkExpirationTimeout: any = null;
var identifiedInThisSession = false;

export function setPostHogUser(posthogInstance: any) {
  const data = useUserStore.getState();
  if (data.logged && !identifiedInThisSession) {
    posthogInstance.identify(data.email, {
      email: data.email,
      is_premium: data.premium,
      subscription_period: data.subscription_period,
    });
    identifiedInThisSession = true;
    const affId = AffiliateId.getState().affiliate_id;
    if (affId)
      posthogInstance.capture(
        '$set',
        {
          $set: {
            'affiliate_id': affId,
          },
          $set_once: { 'initial_affiliate_id': affId },
        }
      )
  }
}

export const refreshUserStore = async () => {
  const userLogged = useUserStore.getState();
  fetchData('/api/logged_user', (data) => {
    if (data.logged) {
      userLogged.set(data);
      if (data.cctk_expires_in) {
        if (cctkExpirationTimeout) clearTimeout(cctkExpirationTimeout);
        cctkExpirationTimeout = setTimeout(() => {
          refreshUserStore();
        }, (data.cctk_expires_in - 5) * 1000);
      }
    }
    else userLogged.set(userDefault);

    if (data.payment_info)
      localStorage.setItem('pagarme_customer_data', JSON.stringify(data.payment_info));
    setRefreshed();

  }, false, setRefreshed);
};

type CallbackFunction = (data: any) => void;

export const fetchDataAfterRefresh = (endpoint: string | string[],
  callback: CallbackFunction,
  showError: boolean = true,
  bypass: boolean = false,
  callbackError: CallbackFunction = (_s) => { },
) => {
  // first item of endpoint is for premium users, second for non-premium
  // if user is not premium, will never be called if passed string

  addRefreshListener(() => {
    ((useUserStore.getState().premium || bypass)
      || typeof endpoint !== 'string'
    ) && fetchData(
      typeof endpoint === 'string' ? endpoint :
        (useUserStore.getState().premium ? endpoint[0] : endpoint[1])
      , callback, showError, callbackError)
  });
}

function addCurrentWindowDomain(url: string) {
  if (url.startsWith('http')) return url;
  return window.location.origin + url;
}

export const postDataAfterRefresh = (endpoint: string | string[],
  data: object,
  callback: CallbackFunction,
  showError: boolean = true,
  bypass: boolean = false,
  callbackError: CallbackFunction = (_s) => { },
) => {
  // first item of endpoint is for premium users, second for non-premium
  // if user is not premium, will never be called if passed string

  addRefreshListener(() => ((useUserStore.getState().premium || bypass)
    || typeof endpoint !== 'string'
  ) && postData(
    typeof endpoint === 'string' ? endpoint :
      (useUserStore.getState().premium ? endpoint[0] : addCurrentWindowDomain(endpoint[1]))
    , data, callback, showError, callbackError));
}

export const fetchDataWithArgsAfterRefresh = (endpoint: string | string[],
  data: object,
  callback: CallbackFunction,
  showError: boolean = true,
  bypass: boolean = false,
  callbackError: CallbackFunction = (_s) => { },
) => {
  addRefreshListener(() => {
    ((useUserStore.getState().premium || bypass)
      || typeof endpoint !== 'string'
    ) && fetchWithArgs(
      typeof endpoint === 'string' ? endpoint :
        (useUserStore.getState().premium ? endpoint[0] : addCurrentWindowDomain(endpoint[1]))
      , data, callback, showError, callbackError)
  });
}